// ===========================================
// A student's email series — every one, including stopped
// ===========================================
//
// ⚠️ CANCELLED AND COMPLETED ENROLMENTS ARE SHOWN DELIBERATELY. "Why did this
// stop?" is the question an admin actually asks, and a list of only the running
// ones cannot answer it. It is also the ONLY place an unsubscribe becomes
// visible: `NO_RECIPIENTS` means every contact on the family opted out, and
// nothing else in this app surfaces that.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as SentIcon,
  ErrorOutline as FailedIcon,
  RemoveCircleOutline as SkippedIcon,
  Schedule as DueIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../common/ConfirmDialog';
import { PermissionGate } from '../../common/PermissionGate';
import {
  useCancelSequenceEnrolment,
  usePauseSequenceEnrolment,
  useResumeSequenceEnrolment,
  useStudentSequenceEnrolments,
} from '../../../hooks/useEmailSequence';
import { apiErrorMessage } from '../../../utils/apiError';
import { format } from 'date-fns';
import { formatUTCDate } from '../../../utils/dateUtils';
import { cancelReasonCopy, completedSummary } from '../../../pages/admin/EmailSequenceEditorPage';
import { formatSendHour } from '../../../pages/admin/EmailSequencesPage';
import type {
  EmailSequenceEnrolment,
  EmailSequenceEnrolmentStep,
} from '../../../types/emailSequence.types';

/**
 * One step's line in the timeline.
 *
 * ⚠️ TWO DIFFERENT DATE SHAPES, AND THEY NEED OPPOSITE TREATMENT.
 *
 *   `sentAt` is a real INSTANT (when Mailgun accepted it), so it is read in the
 *   viewer's own timezone with `format(new Date(...))` — the question an admin
 *   is asking is "what day did this go out", against their own clock.
 *
 *   `dueDay` is a `@db.Date` at UTC MIDNIGHT standing for a calendar day, so it
 *   must be read with UTC getters or it renders a day early for every
 *   Australian viewer.
 *
 * ⚠️ THIS WAS WRONG IN THE OTHER DIRECTION UNTIL /qa PHASE H: `sentAt` went
 * through `formatUTCDate` too. Measured on a UTC+11 host — a send at 22:00 UTC
 * (9am Sydney the NEXT day) displayed as the previous date, and the mismatch
 * covers every send from 10:00 UTC onward, i.e. most of the school day. Avoiding
 * PRE-2's bug (local formatting of a `@db.Date`) by applying UTC formatting
 * everywhere just swaps one wrong day for another. The rule is per FIELD, not
 * per file.
 */
export function stepLine(step: EmailSequenceEnrolmentStep): {
  icon: 'sent' | 'failed' | 'skipped' | 'due';
  text: string;
  detail?: string;
  error?: boolean;
} {
  if (step.state === 'SENT') {
    return {
      icon: 'sent',
      text: step.subject,
      // An instant — local getters, matching how this repo renders every other
      // `sentAt` (five call sites use `toLocaleString`).
      detail: step.sentAt ? `Sent ${format(new Date(step.sentAt), 'dd/MM/yyyy')}` : 'Sent',
    };
  }
  if (step.state === 'FAILED') {
    return {
      icon: 'failed',
      text: step.subject,
      // ⚠️ A failed step is NEVER retried (D-C3). The reason is the only record
      // the family's missing email leaves behind.
      detail: step.failureReason ? `Failed — ${step.failureReason}` : 'Failed',
      error: true,
    };
  }
  if (step.state === 'SKIPPED') {
    return { icon: 'skipped', text: step.subject, detail: 'Skipped — it was already past' };
  }
  return {
    icon: 'due',
    text: step.subject,
    detail: `Due ${formatUTCDate(step.dueDay, { weekday: 'short', day: 'numeric', month: 'short' }, 'en-AU')}`,
  };
}

const STEP_ICON = {
  sent: <SentIcon fontSize="small" color="success" />,
  failed: <FailedIcon fontSize="small" color="error" />,
  skipped: <SkippedIcon fontSize="small" color="disabled" />,
  due: <DueIcon fontSize="small" color="disabled" />,
};

function StatusChip({ enrolment }: { enrolment: EmailSequenceEnrolment }) {
  if (enrolment.status === 'CANCELLED') {
    const copy = cancelReasonCopy(enrolment.cancelReason);
    return (
      <Chip
        size="small"
        variant="outlined"
        color={copy.warn ? 'warning' : 'default'}
        label={copy.text}
      />
    );
  }
  if (enrolment.status === 'COMPLETED') {
    // ⚠️ FILLED, not outlined — the one thing separating it from ACTIVE now
    // that both are green. A finished series and a running one must not read the
    // same at a glance: the first needs nothing from an admin, the second is
    // still sending. Failures stay amber and outlined, since that DOES want
    // attention.
    return enrolment.counts.failed > 0 ? (
      <Chip size="small" variant="outlined" color="warning" label={completedSummary(enrolment)} />
    ) : (
      <Chip size="small" color="success" label={completedSummary(enrolment)} />
    );
  }
  if (enrolment.status === 'PAUSED') {
    return <Chip size="small" variant="outlined" label="Paused" />;
  }
  // Green matches the house convention for a live record — students already use
  // `success` for ACTIVE (`students.helpers.ts:getStudentStatusColor`). This was
  // `primary` (brand blue), which was the odd one out.
  return <Chip size="small" variant="outlined" color="success" label="Active" />;
}

export default function StudentEmailSequencesList({ studentId }: { studentId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: enrolments = [], isLoading, isError, refetch } =
    useStudentSequenceEnrolments(studentId);
  const pauseMutation = usePauseSequenceEnrolment();
  const resumeMutation = useResumeSequenceEnrolment();
  const cancelMutation = useCancelSequenceEnrolment();

  // ⚠️ "WHAT" AND "IS IT OPEN" ARE SEPARATE STATE, deliberately. Nulling one
  // object to close the dialog also blanks its title and message — and MUI keeps
  // the children mounted through the exit transition, so the text visibly
  // empties for ~200ms while the dialog fades (/qa Phase H). Keeping the target
  // until the next open means the copy stays valid all the way out.
  const [confirm, setConfirm] = useState<{
    action: 'pause' | 'resume' | 'cancel';
    enrolment: EmailSequenceEnrolment;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const askToConfirm = (
    action: 'pause' | 'resume' | 'cancel',
    enrolment: EmailSequenceEnrolment
  ) => {
    setConfirm({ action, enrolment });
    setConfirmOpen(true);
  };

  const remaining = (e: EmailSequenceEnrolment) => e.counts.pending;

  const confirmCopy = (): { title: string; message: string; label: string } => {
    if (!confirm) return { title: '', message: '', label: '' };
    const { action, enrolment } = confirm;
    if (action === 'cancel') {
      const n = remaining(enrolment);
      return {
        title: 'Stop this series?',
        // ⚠️ `pending` counts CLAIMED steps too — a step the sweep has already
        // picked up and is sending right now. Stopping the enrolment cannot
        // un-send it, so the copy says "up to" rather than promising all N.
        message: `Stops up to ${n} remaining email${n === 1 ? '' : 's'}. Sent emails are not affected, and one already going out this hour will still arrive.`,
        label: 'Stop series',
      };
    }
    if (action === 'resume') {
      return {
        title: 'Resume this series?',
        // ⚠️ Paused days are NOT made up — the backlog drains one per day. Said
        // here because an admin resuming after a long pause would otherwise
        // expect either a burst or a clean restart, and gets neither.
        message: `Any emails that fell due while paused will send one per day at ${formatSendHour(
          enrolment.sendHour
        )}, starting tomorrow.`,
        label: 'Resume',
      };
    }
    return {
      title: 'Pause this series?',
      message: 'An email already being sent this hour will still go out.',
      label: 'Pause',
    };
  };

  const runAction = async () => {
    if (!confirm) return;
    const { action, enrolment } = confirm;
    const mutation =
      action === 'pause' ? pauseMutation : action === 'resume' ? resumeMutation : cancelMutation;
    try {
      await mutation.mutateAsync(enrolment.id);
      enqueueSnackbar(
        action === 'cancel'
          ? 'Series stopped. Emails already sent are not affected.'
          : action === 'pause'
            ? 'Series paused'
            : 'Series resumed',
        { variant: 'success' }
      );
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, `Could not ${action} this series`), {
        variant: 'error',
      });
    }
    setConfirmOpen(false);
  };

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={64} />
        <Skeleton variant="rectangular" height={64} />
      </Stack>
    );
  }

  // ⚠️ A THIRD STATE. React Query leaves `data` undefined on error as well as
  // while loading, so `?? []` would render "No email series started" for a
  // failed request — telling an admin nothing was ever sent to this family.
  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Could not load this student's email series.
      </Alert>
    );
  }

  if (enrolments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No email series started
      </Typography>
    );
  }

  return (
    <>
      <Stack spacing={1.5}>
        {enrolments.map((enrolment) => (
          <Paper key={enrolment.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500}>
                  {enrolment.sequenceName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {enrolment.counts.sent} of {enrolment.counts.total} sent
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                <StatusChip enrolment={enrolment} />
                <PermissionGate resource="communications_emails" action="write">
                  {enrolment.status === 'ACTIVE' && (
                    <>
                      <Button size="small" onClick={() => askToConfirm('pause', enrolment)}>
                        Pause
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => askToConfirm('cancel', enrolment)}
                      >
                        Stop
                      </Button>
                    </>
                  )}
                  {enrolment.status === 'PAUSED' && (
                    <>
                      <Button
                        size="small"
                        onClick={() => askToConfirm('resume', enrolment)}
                      >
                        Resume
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => askToConfirm('cancel', enrolment)}
                      >
                        Stop
                      </Button>
                    </>
                  )}
                </PermissionGate>
              </Stack>
            </Stack>

            <Stack spacing={0.25} sx={{ mt: 1 }}>
              {enrolment.steps.map((step) => {
                const line = stepLine(step);
                return (
                  <Stack key={step.id} direction="row" spacing={1} alignItems="center">
                    {STEP_ICON[line.icon]}
                    <Typography variant="caption" sx={{ minWidth: 0, flex: 1 }} noWrap>
                      {line.text}
                    </Typography>
                    {line.detail && (
                      <Tooltip title={line.detail}>
                        <Typography
                          variant="caption"
                          color={line.error ? 'error' : 'text.secondary'}
                          noWrap
                          sx={{ maxWidth: '55%' }}
                        >
                          {line.detail}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <ConfirmDialog
        open={confirmOpen}
        title={confirmCopy().title}
        message={confirmCopy().message}
        confirmLabel={confirmCopy().label}
        confirmColor={confirm?.action === 'cancel' ? 'error' : 'primary'}
        onConfirm={runAction}
        onCancel={() => setConfirmOpen(false)}
        loading={
          pauseMutation.isPending || resumeMutation.isPending || cancelMutation.isPending
        }
      />
    </>
  );
}
